// src/components/preAuth/SignupScreen.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Building2, User, Check, Sun, Moon, ArrowRight, RefreshCw, AlertCircle } from 'lucide-react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { auth } from '../../lib/firebase.js';
import { PALETTE, THEME_CLASSES } from '../../lib/theme.js';
import { CONFIG } from '../../lib/config.js';
import rapidLogo from '../../assets/rapidLogo.png';

const signupSchema = z.object({
    tier: z.string().min(1, "Please select a subscription tier."),
    firstName: z.string().min(1, "First name is required."),
    lastName: z.string().min(1, "Last name is required."),
    company: z.string().min(1, "Company name is required."),
    email: z.string().email("Please enter a valid email address."),
    password: z.string().min(8, "Password must be at least 8 characters long.")
});

export default function SignupScreen({ isDark, toggleTheme }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [fetchingTiers, setFetchingTiers] = useState(true);
    const [apiError, setApiError] = useState(null);
    const [availableTiers, setAvailableTiers] = useState([]);
    const [emailSent, setEmailSent] = useState(false);
    const { register, handleSubmit, watch, setValue, formState: { errors, isValid } } = useForm({
        resolver: zodResolver(signupSchema),
        mode: 'onChange',
        defaultValues: {
            firstName: '',
            lastName: '',
            company: '',
            email: '',
            password: '',
            tier: ''
        }
    });
    const selectedTier = watch('tier');

    useEffect(() => {
        const loadTiers = async () => {
            try {
                const res = await fetch(`${CONFIG.API_BASE_URL}/tiers`);
                if (!res.ok) throw new Error("Failed to load pricing tiers");
                const data = await res.json();
                setAvailableTiers(data);
                if (data.length > 0) {
                    setValue('tier', data[0].id, { shouldValidate: true });
                }
            } catch (err) {
                setApiError("Could not load pricing options. Please try again later.");
            } finally {
                setFetchingTiers(false);
            }
        };
        loadTiers();
    }, [setValue]);

    const onSubmit = async (data) => {
        setLoading(true);
        setApiError(null);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, data.email.toLowerCase(), data.password);
            const user = userCredential.user;
            const token = await user.getIdToken();
            const backendRes = await fetch(`${CONFIG.API_BASE_URL}/signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    first_name: data.firstName,
                    last_name: data.lastName,
                    company: data.company,
                    tier: data.tier
                })
            });
            if (!backendRes.ok) {
                const errData = await backendRes.json();
                throw new Error(errData.error || "Failed to provision workspace records");
            }
            await sendEmailVerification(user);
            setEmailSent(true);
        } catch (err) {
            setApiError(err.message || "Failed to register account.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`min-h-screen w-full flex items-center justify-center p-6 relative overflow-hidden transition-colors duration-500 ${THEME_CLASSES.bgMain(isDark)} ${THEME_CLASSES.textPrimary(isDark)}`} style={{ fontFamily: PALETTE.font }}>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[800px] h-[800px] rounded-full blur-[120px] opacity-20 bg-gradient-to-tr" style={{ backgroundImage: `linear-gradient(to top right, ${PALETTE.blue}, ${isDark ? PALETTE.blue2 : PALETTE.greenLight})` }}></div>
            </div>
            <button onClick={toggleTheme} className={`absolute top-6 right-6 w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md transition-all z-20 border ${THEME_CLASSES.bgHighlight(isDark)} ${THEME_CLASSES.borderSubtle(isDark)} ${THEME_CLASSES.textPrimary(isDark)} ${THEME_CLASSES.bgHover(isDark)}`}>
                {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <div className={`relative z-10 w-full max-w-[800px] backdrop-blur-2xl rounded-[32px] p-8 sm:p-12 shadow-2xl transition-all border ${THEME_CLASSES.bgCard(isDark)} ${THEME_CLASSES.borderSubtle(isDark)}`}>
                <div className="flex flex-col items-center text-center mb-10">
                    <img src={rapidLogo} alt="Rapid Logo" className="w-16 h-16 object-contain mb-6" />
                    <h1 className="text-3xl font-bold tracking-tight mb-3">Create Workspace</h1>
                    <p className={`text-sm ${THEME_CLASSES.textSecondary(isDark)}`}>Securely backed by Google Identity & GCP infrastructure.</p>
                </div>
                {emailSent ? (
                    <div className="flex flex-col items-center text-center py-10 space-y-4">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${PALETTE.blue}15` }}>
                            <Mail size={32} style={{ color: PALETTE.blue }} />
                        </div>
                        <h2 className="text-2xl font-bold">Verify Your Identity</h2>
                        <p className={`text-sm ${THEME_CLASSES.textSecondary(isDark)}`}>
                            We sent a secure verification link to <strong>{watch('email')}</strong>.<br/>
                            Click the link in your email to authenticate, then return here to complete checkout.
                        </p>
                        <button
                            onClick={() => navigate('/verify-email')}
                            className="mt-4 px-6 py-3 text-white rounded-xl font-bold transition-transform hover:scale-105"
                            style={{ backgroundColor: PALETTE.blue }}
                        >
                            I Have Verified My Email
                        </button>
                    </div>
                ) : fetchingTiers ? (
                    <div className="flex justify-center items-center py-10">
                        <RefreshCw className="animate-spin" size={32} style={{ color: PALETTE.blue }} />
                    </div>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                            {availableTiers.map((tier) => (
                                <div key={tier.id} onClick={() => setValue('tier', tier.id, { shouldValidate: true })} className={`cursor-pointer rounded-2xl p-5 border-2 transition-all ${selectedTier === tier.id ? `border-blue-500 bg-blue-500/10` : `border-transparent ${THEME_CLASSES.bgHighlight(isDark)} hover:border-blue-500/50`}`}>
                                    <h3 className="text-lg font-bold">{tier.name}</h3>
                                    <div className="text-2xl font-bold my-3">${tier.price_monthly}<span className="text-sm font-normal text-slate-500">/mo</span></div>
                                    <div className="text-xs font-semibold text-blue-500 mb-2 bg-blue-500/10 inline-block px-2 py-1 rounded">30-Day Free Trial</div>
                                    <ul className="space-y-2 mt-4">
                                        <li className="text-xs flex items-center gap-2"><Check size={14} className="text-blue-500" /> {tier.storage_gb} GB Storage</li>
                                        <li className="text-xs flex items-center gap-2"><Check size={14} className="text-blue-500" /> Up to {tier.max_data_sources} Sources</li>
                                    </ul>
                                </div>
                            ))}
                        </div>
                        {errors.tier && <p className="text-red-500 text-xs font-bold text-center -mt-4 mb-4">{errors.tier.message}</p>}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <div className="relative group">
                                    <User className={`absolute left-4 top-3 z-10 ${THEME_CLASSES.textSecondary(isDark)}`} size={18} />
                                    <input type="text" placeholder="First Name" {...register('firstName')} className={`${THEME_CLASSES.input(isDark)} pl-11`} />
                                </div>
                                {errors.firstName && <p className="text-red-500 text-xs font-bold mt-1 ml-1">{errors.firstName.message}</p>}
                            </div>
                            <div>
                                <div className="relative group">
                                    <User className={`absolute left-4 top-3 z-10 ${THEME_CLASSES.textSecondary(isDark)}`} size={18} />
                                    <input type="text" placeholder="Last Name" {...register('lastName')} className={`${THEME_CLASSES.input(isDark)} pl-11`} />
                                </div>
                                {errors.lastName && <p className="text-red-500 text-xs font-bold mt-1 ml-1">{errors.lastName.message}</p>}
                            </div>
                        </div>
                        <div>
                            <div className="relative group">
                                <Building2 className={`absolute left-4 top-3 z-10 ${THEME_CLASSES.textSecondary(isDark)}`} size={18} />
                                <input type="text" placeholder="Company Name" {...register('company')} className={`${THEME_CLASSES.input(isDark)} pl-11`} />
                            </div>
                            {errors.company && <p className="text-red-500 text-xs font-bold mt-1 ml-1">{errors.company.message}</p>}
                        </div>
                        <div>
                            <div className="relative group">
                                <Mail className={`absolute left-4 top-3 z-10 ${THEME_CLASSES.textSecondary(isDark)}`} size={18} />
                                <input type="email" placeholder="Email Address" {...register('email')} className={`${THEME_CLASSES.input(isDark)} pl-11`} />
                            </div>
                            {errors.email && <p className="text-red-500 text-xs font-bold mt-1 ml-1">{errors.email.message}</p>}
                        </div>
                        <div>
                            <div className="relative group">
                                <Lock className={`absolute left-4 top-3 z-10 ${THEME_CLASSES.textSecondary(isDark)}`} size={18} />
                                <input type="password" placeholder="Password" {...register('password')} className={`${THEME_CLASSES.input(isDark)} pl-11`} />
                            </div>
                            {errors.password && <p className="text-red-500 text-xs font-bold mt-1 ml-1">{errors.password.message}</p>}
                        </div>
                        {apiError && <div className={`flex items-center space-x-2 text-xs font-medium p-3 rounded-lg border ${isDark ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-red-50 text-red-600 border-red-200'}`}><AlertCircle size={16} /><span>{apiError}</span></div>}
                        <button type="submit" disabled={loading || availableTiers.length === 0} className="w-full h-12 rounded-2xl font-bold text-white shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center space-x-2 mt-4 text-[15px] disabled:opacity-50" style={{ backgroundColor: PALETTE.blue, boxShadow: `0 8px 20px -6px ${PALETTE.blue}50` }}>
                            {loading ? <RefreshCw className="animate-spin" size={20} /> : <><span>Start 30-Day Free Trial</span><ArrowRight size={18} /></>}
                        </button>
                    </form>
                )}
                {!emailSent && (
                    <div className="mt-8 text-center">
                        <p className={`text-sm font-medium ${THEME_CLASSES.textSecondary(isDark)}`}>Already have an account? <button onClick={() => navigate('/login')} className="font-bold hover:underline ml-1" style={{ color: PALETTE.blue }}>Sign in</button></p>
                    </div>
                )}
            </div>
        </div>
    );
}